<template>
  <div class="container mt-2 mt-md-4 px-2 px-md-3 gastos-view">
    <div class="gastos-heading mb-3 mb-md-4">
      <div>
        <h1 class="gastos-title mb-0">Gastos</h1>
        <p class="gastos-mes-label mb-0">Este mes</p>
      </div>
      <div class="gastos-mes-totales">
        <p class="gastos-mes-total mb-0">{{ totalMes }} €</p>
        <p
          v-if="gastosListos"
          class="gastos-mes-comparacion mb-0"
          :class="claseComparacion"
        >
          {{ textoComparacion.lineaDelta }}
        </p>
        <p v-if="gastosListos" class="gastos-mes-comparacion-ctx mb-0">
          {{ textoComparacion.lineaCtx }}
        </p>
      </div>
    </div>
    
    <!-- Alerta de éxito -->
    <div v-if="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      {{ successMessage }}
      <button type="button" class="btn-close" @click="successMessage = ''" aria-label="Close"></button>
    </div>
    
    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div class="row">
      <div class="col-12">
        <gastos-pendientes-compartidos
          @aceptado="handlePendienteAceptado"
          @descartado="handlePendienteDescartado"
        />
        <gasto-form
          v-if="mostrarFormulario"
          @gasto-added="handleGastoAdded"
        />
        <gasto-list 
          :gastos="gastos" 
          @gasto-deleted="handleGastoDeleted"
          @gasto-updated="handleGastoUpdated"
          @pagina-cambiada="actualizarGastos"
          @nuevo-gasto="abrirFormulario"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, computed, watch, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import GastoForm from '../components/GastoForm.vue';
import GastoList from '../components/GastoList.vue';
import GastosPendientesCompartidos from '../components/GastosPendientesCompartidos.vue';
import { debeAbrirNuevoGasto, esNotificacionGasto } from '../utils/atajoPwa';
import {
  comparacionMesAnterior as calcularComparacionMesAnterior,
  textoComparacionMesAnterior,
  claseComparacionMesAnterior
} from '../utils/comparacionMesAnterior';

export default {
  name: 'HomeView',
  components: {
    GastoForm,
    GastoList,
    GastosPendientesCompartidos
  },
  
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const gastos = computed(() => store.getters.getGastos);
    const error = ref(null);
    const successMessage = ref('');
    const mostrarFormulario = ref(false);
    const gastosListos = ref(false);

    const abrirFormulario = () => {
      mostrarFormulario.value = true;
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
    };

    watch(
      () => [route.path, route.query.nuevo, route.query.notificacion, route.meta && route.meta.abrirFormulario],
      () => {
        if (debeAbrirNuevoGasto(route)) {
          abrirFormulario();
          if (route.path === '/nuevo-gasto' || route.query.nuevo) {
            router.replace({ path: '/' });
          }
          return;
        }
        if (esNotificacionGasto(route) && route.query.notificacion) {
          store.dispatch('fetchNotificacionesGrupo').catch(() => null);
        }
      },
      { immediate: true }
    );
    const diaActual = new Date().getDate();
    const mesActual = new Date().getMonth();
    const anoActual = new Date().getFullYear();
    
    // Memoizar los gastos filtrados por año para evitar recálculos
    const gastosAnoActual = computed(() => {
      return gastos.value.filter(g => {
        const fecha = new Date(g.fecha);
        return fecha.getFullYear() === anoActual;
      });
    });
    
    const gastosAnoPasado = computed(() => {
      return gastos.value.filter(g => {
        const fecha = new Date(g.fecha);
        return fecha.getFullYear() === anoActual - 1;
      });
    });
    
    // Memoizar los gastos filtrados por mes para evitar recálculos
    const gastosMesActual = computed(() => {
      return gastosAnoActual.value.filter(g => {
        const fecha = new Date(g.fecha);
        return fecha.getMonth() === mesActual;
      });
    });
    
    const gastosMesPasado = computed(() => {
      const mesPasado = mesActual === 0 ? 11 : mesActual - 1;
      const anoPasado = mesActual === 0 ? anoActual - 1 : anoActual;
      
      return gastos.value.filter(g => {
        const fecha = new Date(g.fecha);
        return fecha.getMonth() === mesPasado && 
               fecha.getFullYear() === anoPasado &&
               fecha.getDate() <= diaActual;
      });
    });
    
    const gastosMesAnoPasado = computed(() => {
      return gastosAnoPasado.value.filter(g => {
        const fecha = new Date(g.fecha);
        return fecha.getMonth() === mesActual &&
               fecha.getDate() <= diaActual;
      });
    });
    
    // Calcular totales a partir de los gastos filtrados
    const totalAno = computed(() => {
      return gastosAnoActual.value
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });
    
    const totalAnoPasado = computed(() => {
      return gastosAnoPasado.value
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });
    
    const totalMes = computed(() => {
      return gastosMesActual.value
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });
    
    const totalMesPasado = computed(() => {
      return gastosMesPasado.value
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });
    
    const totalMesAnoPasado = computed(() => {
      return gastosMesAnoPasado.value
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });

    const comparacionMes = computed(() => calcularComparacionMesAnterior(gastos.value));
    const textoComparacion = computed(() => textoComparacionMesAnterior(comparacionMes.value));
    const claseComparacion = computed(() => claseComparacionMesAnterior(comparacionMes.value.delta));

    const getBadgeClass = (comparativo, actual) => {
      if (comparativo > actual) return 'bg-success';
      if (comparativo < actual) return 'bg-danger';
      return 'bg-info';
    };

    const getProgressClass = (comparativo, actual) => {
      if (comparativo > actual) return 'bg-success';
      if (comparativo < actual) return 'bg-danger';
      return 'bg-info';
    };

    const getProgressWidth = (comparativo, actual) => {
      const porcentaje = (comparativo / actual) * 100;
      return `${Math.min(100, porcentaje)}%`;
    };
    
    const loadGastos = async () => {
      try {
        error.value = null;
        await store.dispatch('fetchUltimosGastos');
      } catch (err) {
        console.error('Error cargando gastos:', err);
        error.value = 'Error al cargar los gastos';
      } finally {
        gastosListos.value = true;
      }
    };
    
    // Nueva función para actualizar los gastos cuando cambia la página
    const actualizarGastos = (nuevosGastos) => {
      console.log('Actualizando gastos con nuevos datos de página:', nuevosGastos?.length || 0);
      // No reemplazamos todos los gastos, solo actualizamos la vista si es necesario
      // Esto evita que se pierda la paginación
      if (nuevosGastos && Array.isArray(nuevosGastos) && nuevosGastos.length > 0) {
        // Solo actualizamos si realmente hay datos nuevos
        console.log('Actualizando vista con nuevos gastos');
      }
    };
    
    // Nueva función para manejar cuando se añade un gasto
    const handleGastoAdded = async (gastoData) => {
      try {
        console.log('Gasto añadido, actualizando lista...', gastoData);
        console.log('Tipo de gastoData:', typeof gastoData);
        console.log('¿gastoData es un objeto?', typeof gastoData === 'object');
        console.log('Propiedades de gastoData:', gastoData ? Object.keys(gastoData) : 'null o undefined');
        
        if (gastoData && gastoData.id) {
          console.log('ID del gasto añadido:', gastoData.id);
          console.log('Tipo del ID:', typeof gastoData.id);
          
          // Asegurarse de que el ID sea un número
          const gastoConIdVerificado = {
            ...gastoData,
            id: typeof gastoData.id === 'string' ? parseInt(gastoData.id, 10) : gastoData.id
          };
          
          console.log('Gasto con ID verificado antes de añadir al store:', gastoConIdVerificado);
          
          // Añadir el gasto al estado
          await store.dispatch('addGasto', gastoConIdVerificado);
          
          // Cerrar formulario y mostrar mensaje de éxito
          mostrarFormulario.value = false;
          successMessage.value = '¡Gasto añadido correctamente!';
        } else {
          console.warn('No se recibió un ID válido del gasto añadido');
          console.warn('Datos recibidos:', JSON.stringify(gastoData, null, 2));
          
          // Si no tenemos un ID válido, recargamos todos los gastos
          await store.dispatch('fetchUltimosGastos');
          
          // Mostrar mensaje de advertencia
          successMessage.value = 'Gasto añadido, pero hubo un problema con el ID. Se han recargado los datos.';
        }
        
        // Ocultar el mensaje después de 5 segundos
        setTimeout(() => {
          successMessage.value = '';
        }, 5000);
      } catch (err) {
        console.error('Error al actualizar gastos después de añadir:', err);
        error.value = 'Error al actualizar la lista de gastos';
      }
    };
    
    // Función para manejar la eliminación de un gasto
    const handleGastoDeleted = async (gastoId) => {
      try {
        console.log('Gasto eliminado, actualizando lista...', gastoId);
        console.log('Tipo de gastoId:', typeof gastoId);
        
        if (gastoId) {
          console.log('ID del gasto eliminado:', gastoId);
          
          // Asegurarse de que el ID sea un número
          const idVerificado = typeof gastoId === 'string' ? parseInt(gastoId, 10) : gastoId;
          console.log('ID verificado antes de eliminar:', idVerificado);
          
          // Eliminar el gasto del estado
          await store.dispatch('removeGasto', idVerificado);
        } else {
          console.warn('No se recibió un ID válido del gasto eliminado');
          
          // Si no tenemos un ID válido, recargamos todos los gastos
          await store.dispatch('fetchUltimosGastos');
        }
        
        // Mostrar mensaje de éxito
        successMessage.value = '¡Gasto eliminado correctamente!';
        
        // Ocultar el mensaje después de 5 segundos
        setTimeout(() => {
          successMessage.value = '';
        }, 5000);
      } catch (err) {
        console.error('Error al actualizar gastos después de eliminar:', err);
        error.value = 'Error al actualizar la lista de gastos';
      }
    };
    
    // Función para manejar la actualización de un gasto
    const handleGastoUpdated = async () => {
      try {
        console.log('Gasto actualizado, actualizando lista...');
        
        // Recargar los gastos desde el servidor
        await store.dispatch('fetchGastos');
        
        // Mostrar mensaje de éxito
        successMessage.value = '¡Gasto actualizado correctamente!';
        
        // Ocultar el mensaje después de 5 segundos
        setTimeout(() => {
          successMessage.value = '';
        }, 5000);
      } catch (err) {
        console.error('Error al actualizar gastos después de editar:', err);
        error.value = 'Error al actualizar la lista de gastos';
      }
    };
    
    // Reducir la profundidad del watch para mejorar rendimiento
    watch(gastos, (newGastos) => {
      console.log('Gastos actualizados, cantidad:', newGastos?.length || 0);
    }, { deep: false });
    
    const handlePendienteAceptado = () => {
      successMessage.value = 'Gasto añadido a tu lista.';
      setTimeout(() => { successMessage.value = ''; }, 5000);
    };

    const handlePendienteDescartado = () => {
      successMessage.value = 'Gasto descartado.';
      setTimeout(() => { successMessage.value = ''; }, 5000);
    };

    onMounted(() => {
      loadGastos();
    });
    
    return {
      gastos,
      totalAno,
      totalAnoPasado,
      totalMes,
      totalMesPasado,
      totalMesAnoPasado,
      textoComparacion,
      claseComparacion,
      gastosListos,
      diaActual,
      getBadgeClass,
      getProgressClass,
      getProgressWidth,
      error,
      successMessage,
      mostrarFormulario,
      abrirFormulario,
      loadGastos,
      handleGastoAdded,
      handleGastoDeleted,
      handleGastoUpdated,
      handlePendienteAceptado,
      handlePendienteDescartado,
      actualizarGastos,
    };
  }
};
</script>

<style scoped>
.gastos-view {
  max-width: 100%;
  overflow-x: hidden;
}

.gastos-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.gastos-mes-totales {
  text-align: right;
  min-width: 0;
}

.gastos-title {
  font-size: 1.55rem;
  letter-spacing: -0.03em;
  color: var(--tinta, #1c241c);
}

.gastos-mes-label {
  color: var(--tinta-suave, #5c675e);
  font-size: 0.85rem;
}

.gastos-mes-total {
  font-size: 1.85rem;
  line-height: 1;
  color: var(--gasto, #9a3412);
}

.gastos-mes-comparacion {
  margin-top: 0.4rem;
  font-size: 0.8rem;
  font-weight: 650;
  line-height: 1.25;
  letter-spacing: -0.01em;
}

.gastos-mes-comparacion--mas {
  color: var(--gasto, #9a3412);
}

.gastos-mes-comparacion--menos {
  color: var(--ingreso, #1f6b45);
}

.gastos-mes-comparacion--igual {
  color: var(--tinta-suave, #5c675e);
}

.gastos-mes-comparacion-ctx {
  margin-top: 0.12rem;
  color: var(--tinta-suave, #5c675e);
  font-size: 0.72rem;
  line-height: 1.3;
  max-width: 16rem;
  margin-left: auto;
}

@media (min-width: 768px) {
  .gastos-title {
    font-size: 1.85rem;
  }

  .gastos-mes-total {
    font-size: 2.25rem;
  }
}

@media (max-width: 767.98px) {
  .gastos-view .container {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}

.badge {
  font-size: 1rem;
  padding: 0.5rem 1rem;
}

.list-group-item {
  border-left: none;
  border-right: none;
}

.list-group-item:first-child {
  border-top: none;
}

.list-group-item:last-child {
  border-bottom: none;
}

.progress {
  background-color: #e9ecef;
  border-radius: 0.25rem;
}
</style>